import { get, isEmpty, isNull } from 'lodash';
import moment from 'moment-timezone';

import { CLAIM_STATUS, PROCEDURE_STATUSES } from 'containers/data-structure';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { SearchClaims } from 'containers/IdentificationPage/typings';
import { CANCEL } from 'containers/IdentificationPage/constants';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { isNotRcRisk } from 'services/utils';
import { OPENING_STATE, OPENING_TYPE } from 'services/constants';
import messages from 'containers/IdentificationPage/messages';
import { CLAIM_TYPE } from 'containers/IdentificationPage/SearchResults/ClaimWithNotIndexedPV/constants';
import { translate } from 'containers/services/translate';

export const isSearchResultDefined = (searchClaims: SearchClaims) => {
  return (
    !isEmpty(searchClaims.sagis?.policy) &&
    (!isEmpty(searchClaims.sagis?.claims) ||
      !isEmpty(
        searchClaims.corporeal?.filter(c => c.status !== CLAIM_STATUS.REJECTED),
      ))
  );
};

export const isClaimRejected = (searchClaims: SearchClaims) =>
  !isEmpty(
    searchClaims.corporeal?.filter(c => c.status === CLAIM_STATUS.REJECTED),
  );
export const isClaimOpen = (listings: any, searchClaims: SearchClaims) => {
  const filtredCorporelClaims = get(searchClaims, 'sagis.claims', []).filter(
    (el: any) =>
      get(el, 'code') &&
      ((!isNotRcRisk(listings, el?.code) &&
        el?.claimType != CLAIM_TYPE.CORPOREL) ||
        el?.claimType != CLAIM_TYPE.CORPOREL),
  );
  const validatedClaims = searchClaims.corporeal?.find(
    (c: any) =>
      c.status === CLAIM_STATUS.VALIDATED &&
      c.openingState === OPENING_STATE.COMPLETED,
  );
  const pendingClaims = searchClaims.corporeal?.find(
    (c: any) =>
      c.status === CLAIM_STATUS.PENDING ||
      c.status === CLAIM_STATUS.PENDING_INFO_MODIFICATION,
  );
  const pendingApprovalClaims = searchClaims.corporeal?.find(
    (c: any) => c.status === CLAIM_STATUS.PENDING_APPROVAL,
  );

  if (!isEmpty(validatedClaims)) {
    return {
      status: CLAIM_STATUS.VALIDATED,
      isCorpo: true,
    };
  } else if (!isEmpty(pendingClaims)) {
    return {
      status: CLAIM_STATUS.PENDING,
      isCorpo: true,
    };
  } else if (!isEmpty(pendingApprovalClaims)) {
    return {
      status: CLAIM_STATUS.PENDING_APPROVAL,
      isCorpo: true,
    };
  } else if (
    !isEmpty(filtredCorporelClaims) &&
    !!filtredCorporelClaims?.find((c: any) =>
      get(c, 'offeredRisks', []).find((el: any) => el.riskName === rcCoporeal),
    )
  ) {
    return {
      isCorpo: false,
    };
  }
  return false;
};

export const isCorporealClaimRejected = (searchClaims: SearchClaims) =>
  !isEmpty(
    searchClaims.corporeal?.filter(
      c => c.status === CLAIM_STATUS.REJECTED && !c.claimNumber,
    ),
  );
export const isClaimCancelledHasCorporel = (searchClaims: SearchClaims) => {
  return !isEmpty(searchClaims.sagis?.claims?.filter(c => c.status === CANCEL))
    ? !isEmpty(
        searchClaims.sagis?.claims?.filter(
          c => c.claimType === CLAIM_TYPE.CORPOREL,
        ),
      )
    : !!isEmpty(searchClaims.sagis?.claims?.filter(c => c.status === CANCEL));
};
export const helper = {
  isSearchResultDefined,
  isClaimRejected,
  isClaimCancelledHasCorporel,
};

export const shouldDisplayExistingClaimIndexationPanel = (
  searchClaims: SearchClaims,
) => {
  return (
    helper.isSearchResultDefined(searchClaims) &&
    helper.isClaimCancelledHasCorporel(searchClaims) &&
    featureService.hasFeature(FEATURES.LAWYER_INDEXATION)
  );
};

export const coUtil = { shouldDisplayExistingClaimIndexationPanel };

export const shouldDisplaySearchResult = (
  listings: any,
  searchClaims: SearchClaims,
) => {
  return (
    (!isNull(searchClaims.lossDate) &&
      !coUtil.shouldDisplayExistingClaimIndexationPanel(searchClaims)) ||
    (isClaimOpen(listings, searchClaims) &&
      isEmpty(get(searchClaims, 'sagis.claims', [])))
  );
};

export const dateStringToLocalDate = (s: string | null) => {
  if (!s) return null;

  return moment(s)
    .tz('Africa/Casablanca')
    .format('YYYY-MM-DD');
};
export const validateSearchQuery = (
  searchQuery: { [key: string]: any },
  optionnel: string[],
) => {
  let result = false;
  Object.keys(searchQuery).forEach(query => {
    if (searchQuery[query] !== null && !optionnel.includes(query)) {
      result = true;
    }
  });
  return result;
};

export const filterNotRcClaims = (searchClaims: any, listings: any) => {
  if (!isEmpty(searchClaims?.sagis?.claims)) {
    return {
      ...searchClaims,
      sagis: {
        ...searchClaims?.sagis,
        claims: searchClaims?.sagis?.claims.filter(
          (el: any) => !isNotRcRisk(listings, el?.code),
        ),
      },
    };
  } else {
    return searchClaims;
  }
};

export const getRejectionClaimElement = (
  corporealClaim: any,
  listings: any,
) => {
  return corporealClaim.flatMap((item: any) => {
    if (!isEmpty(item.rejectionReasons)) {
      return item.rejectionReasons.map((el: any) => {
        const titleRejection = get(
          get(listings, 'rejectionReasons', []).find(
            (op: any) => op.id === el.rejectionReasonId,
          ),
          'title',
          '',
        );
        if (titleRejection === 'Autre') {
          return (
            titleRejection + '( ' + get(item, 'otherRejectionReason') + ' )'
          );
        }
        return titleRejection;
      });
    } else {
      return '';
    }
  });
};

export const isBasicManagementClaim = (searchClaims: SearchClaims) =>
  isEmpty(searchClaims.corporeal) &&
  searchClaims.sagis.claims.find((c: any) =>
    get(c, 'offeredRisks', []).find((el: any) => el.riskName === rcCoporeal),
  );

export const isNotCompleteClaim = (searchClaims: SearchClaims) =>
  featureService.hasFeature(FEATURES.OPENING_BY_USAGE) &&
  !isEmpty(searchClaims.corporeal) &&
  !isEmpty(
    searchClaims.corporeal.find(
      (c: any) =>
        c.openingState === OPENING_STATE.NOT_COMPLETED &&
        c.openingType === OPENING_TYPE.USAGE,
    ),
  );

export const renderWarningMessage = (props: any) => {
  let messageToDisplay;
  if (props.isOpen && !get(props.isOpen, 'isCorpo')) {
    messageToDisplay = translate(messages.openedClaimWarningTitle, props);
  } else if (
    props.isOpen &&
    get(props.isOpen, 'isCorpo') &&
    get(props.isOpen, 'status') === PROCEDURE_STATUSES.VALIDATED
  ) {
    messageToDisplay = translate(
      { ...messages.openedClaimWarningValidatedTitle },
      props,
    );
  } else if (
    props.isOpen &&
    get(props.isOpen, 'isCorpo') &&
    get(props.isOpen, 'status') === PROCEDURE_STATUSES.PENDING
  ) {
    messageToDisplay = translate(
      { ...messages.openedClaimWarningPendingTitle },
      props,
    );
  } else if (
    props.isOpen &&
    get(props.isOpen, 'isCorpo') &&
    get(props.isOpen, 'status') === PROCEDURE_STATUSES.PENDING_APPROVAL
  ) {
    messageToDisplay = translate(
      { ...messages.pendingApprovalWarningTilte },
      props,
    );
  } else if (props.isRejected && !props.enablePreOpenClaim) {
    messageToDisplay = translate({ ...messages.claimRejectedFound }, props, {
      reasons: getRejectionClaimElement(
        props.corporealClaim,
        props.listings,
      ).map((reason: any) => reason + ', '),
    });
  } else if (props.isRejected && props.enablePreOpenClaim) {
    messageToDisplay = translate(
      { ...messages.otherRejectedClaimExist },
      props,
      {
        reasons: getRejectionClaimElement(
          props.corporealClaim,
          props.listings,
        ).map((reason: any) => reason + ', '),
      },
    );
  } else if (!props.policyExists) {
    messageToDisplay = translate(
      { ...messages.noPolicyAssociatedWasFound },
      props,
    );
  } else if (!props.claimExists) {
    messageToDisplay = translate(
      { ...messages.noClaimAssociatedWasFound },
      props,
    );
  }
  return messageToDisplay;
};
